import {
  adjustGoalAverage,
  adjustHomeAdvantage,
  adjustDrawFactor,
  normalizeGoalTotals,
  normalizeHomeAdvantage,
} from "./adjustTeamStrengths.js";
import { computeInterdependentValues } from "./computeInterdependentValues.js";
export const leagueFields = [
  "stored_goal_average",
  "exp_home_factor",
  "drawfactor",
];
export const teamFields = ["total_strength", "total_goals"];
export function positive(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
export function previewLeagueDraft(teams, original, target) {
  let rows = teams.map((t) => ({
    ...t,
    total_goals: t.for_total + t.against_total,
  }));
  for (const key of leagueFields)
    if (!positive(target[key])) throw Error("INVALID_DRAFT");
  if (target.exp_home_factor >= 2) throw Error("INVALID_DRAFT");
  if (target.stored_goal_average !== original.stored_goal_average)
    rows = adjustGoalAverage(rows, target.stored_goal_average);
  if (target.exp_home_factor !== original.exp_home_factor)
    rows = adjustHomeAdvantage(rows, target.exp_home_factor);
  if (target.drawfactor !== original.drawfactor)
    rows = adjustDrawFactor(rows, target.drawfactor);
  for (const row of rows)
    if (
      !positive(row.for_home) ||
      !positive(row.against_home) ||
      !positive(row.for_away) ||
      !positive(row.against_away)
    )
      throw Error("INVALID_DRAFT");
  return rows;
}
export function previewTeamDraft(rows, targets = {}) {
  const ids = new Set(rows.map((r) => String(r.team_id)));
  for (const id of Object.keys(targets))
    if (!ids.has(id)) throw Error("FOREIGN_TEAM");
  return rows.map((row) => {
    let next = { ...row };
    for (const field of teamFields) {
      const value = targets[row.team_id]?.[field];
      if (value === undefined) continue;
      if (!positive(value)) throw Error("INVALID_DRAFT");
      next = computeInterdependentValues({ ...next, [field]: value }, next);
    }
    return next;
  });
}
export function draftSummary(
  original,
  preview,
  final,
  leagueTargets,
  suggestions = {},
  leagueSuggestions = []
) {
  const counts = {
    league: 0,
    strength: 0,
    goal_sum: 0,
    accepted: 0,
    manual: 0,
    ignored: 0,
  };
  for (const key of leagueFields) {
    const proposal = leagueSuggestions.find((s) => s.field === key)?.proposed;
    if (leagueTargets[key] !== original.parameters[key]) {
      counts.league++;
      counts[leagueTargets[key] === proposal ? "accepted" : "manual"]++;
    } else if (proposal !== undefined && proposal !== original.parameters[key])
      counts.ignored++;
  }
  for (const row of final) {
    const base = preview.find((r) => r.team_id === row.team_id);
    for (const key of teamFields) {
      const suggestion = suggestions[row.team_id]?.[key]?.proposed;
      if (row[key] !== base[key]) {
        counts[key === "total_strength" ? "strength" : "goal_sum"]++;
        counts[row[key] === suggestion ? "accepted" : "manual"]++;
      } else if (suggestion !== undefined && suggestion !== base[key])
        counts.ignored++;
    }
  }
  return counts;
}

// The editor and API deliberately share the same raw-value operations.
export const detailFields = [
  "for_home",
  "against_home",
  "for_away",
  "against_away",
];
export function previewDetailDraft(teams, targets, drawfactor, { normalize = true } = {}) {
  const ids = new Set(teams.map((t) => String(t.team_id)));
  if (
    !targets ||
    Object.keys(targets).length !== ids.size ||
    Object.keys(targets).some((id) => !ids.has(id)) ||
    !positive(drawfactor)
  )
    throw Error("INVALID_DRAFT");
  let rows = teams.map((team) => {
    let row = { ...team, total_goals: team.for_total + team.against_total };
    const target = targets[team.team_id];
    if (!target || Object.keys(target).some((k) => !detailFields.includes(k)))
      throw Error("INVALID_DRAFT");
    for (const field of detailFields) {
      if (!positive(target[field])) throw Error("INVALID_DRAFT");
      row = computeInterdependentValues(
        { ...row, [field]: target[field] },
        row
      );
    }
    return row;
  });
  if (normalize) rows = normalizeHomeAdvantage(normalizeGoalTotals(rows));
  rows = adjustDrawFactor(rows, drawfactor);
  for (const row of rows)
    if (
      [
        ...detailFields,
        "for_total",
        "against_total",
        "total_strength",
        "total_goals",
        "draw_factor",
      ].some((k) => !positive(row[k]))
    )
      throw Error("INVALID_DRAFT");
  return rows;
}
export function detailSummary(teams, rows) {
  const changes = rows.flatMap((row) => {
    const before = teams.find((t) => t.team_id === row.team_id).setting;
    return detailFields
      .filter((field) => Math.abs(row[field] - before[field]) > 1e-10)
      .map((field) => ({
        team_id: row.team_id,
        field,
        before: before[field],
        after: row[field],
        delta: row[field] - before[field],
      }));
  });
  return {
    changed_details: changes.length,
    largest_details: [...changes]
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 10),
    ...Object.fromEntries(
      ["total_strength", "total_goals"].map((field) => [
        field,
        rows
          .map((row) => ({
            team_id: row.team_id,
            before: teams.find((t) => t.team_id === row.team_id).setting[field],
            after: row[field],
          }))
          .sort(
            (a, b) =>
              Math.abs(b.after - b.before) - Math.abs(a.after - a.before)
          )
          .slice(0, 3),
      ])
    ),
  };
}
